import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";

import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  listReminderSchedules,
  getReminderScheduleData,
  createReminderSchedule,
  updateReminderSchedule,
  deleteReminderSchedules,
} from "./esignReminderScheduleService";

const BASIS_OPTIONS = [
  { value: "after_send", label: "days after sending" },
  { value: "before_expiry", label: "days before expiry" },
];

const CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Email + SMS" },
];

const emptyForm = {
  Name: "",
  Description: "",
  FlagDefault: 0,
  FlagActive: 1,
  MaxReminders: "",
};

const emptyStepForm = {
  StepOrder: 1,
  OffsetDays: "",
  OffsetBasis: "after_send",
  Channel: "email",
  Subject: "",
  Body: "",
  FlagActive: 1,
};

// A short human summary of a step, e.g. "Day 3 · Email".
const stepSummary = (step) => {
  const basis = BASIS_OPTIONS.find((b) => b.value === step.OffsetBasis);
  const channel = CHANNEL_OPTIONS.find((c) => c.value === step.Channel);
  return `${step.OffsetDays || 0} ${basis?.label ?? "days"} · ${
    channel?.label ?? step.Channel
  }`;
};

export default function ESignReminderSchedules() {
  const [scheduleList, setScheduleList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  // Steps are edited locally and saved with the schedule in one call.
  const [steps, setSteps] = useState([]);
  const nextStepKey = useRef(1);
  const [saving, setSaving] = useState(false);

  const [stepModal, setStepModal] = useState(false);
  const [editingStepKey, setEditingStepKey] = useState(null);
  const [stepForm, setStepForm] = useState(emptyStepForm);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = async () => {
    setLoadingList(true);
    try {
      const res = await listReminderSchedules();
      if (res?.status === 200) {
        setScheduleList(res.esignreminderschedule_list ?? []);
      } else {
        toast.error(res?.message || "Failed to load reminder schedules");
      }
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const displayedSteps = useMemo(
    () =>
      [...steps].sort(
        (a, b) =>
          Number(a.StepOrder) - Number(b.StepOrder) ||
          Number(a.OffsetDays) - Number(b.OffsetDays)
      ),
    [steps]
  );

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const resetEditor = () => {
    setSelectedId(null);
    setIsNew(false);
    setForm(emptyForm);
    setSteps([]);
  };

  const handleNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setForm(emptyForm);
    setSteps([]);
  };

  const handleSelect = async (id) => {
    setIsNew(false);
    setSelectedId(id);
    const res = await getReminderScheduleData(id);
    if (res?.status === 200 && res.esignreminderschedule_data) {
      const data = res.esignreminderschedule_data;
      setForm({
        Name: data.Name ?? "",
        Description: data.Description ?? "",
        FlagDefault: Number(data.FlagDefault) ? 1 : 0,
        FlagActive: Number(data.FlagActive) ? 1 : 0,
        MaxReminders: data.MaxReminders ?? "",
      });
      setSteps(
        (data.esignreminderschedulestep_list ?? []).map((s) => ({
          _key: nextStepKey.current++,
          Id: s.Id,
          StepOrder: s.StepOrder ?? 1,
          OffsetDays: s.OffsetDays ?? 0,
          OffsetBasis: s.OffsetBasis ?? "after_send",
          Channel: s.Channel ?? "email",
          Subject: s.Subject ?? "",
          Body: s.Body ?? "",
          FlagActive: Number(s.FlagActive) ? 1 : 0,
        }))
      );
    } else {
      toast.error(res?.message || "Failed to load schedule");
    }
  };

  // Steps (local)
  const setStepField = (field, value) =>
    setStepForm((prev) => ({ ...prev, [field]: value }));

  const handleNewStep = () => {
    const nextOrder =
      displayedSteps.length > 0
        ? Math.max(...displayedSteps.map((s) => Number(s.StepOrder) || 0)) + 1
        : 1;
    setEditingStepKey(null);
    setStepForm({ ...emptyStepForm, StepOrder: nextOrder });
    setStepModal(true);
  };

  const handleEditStep = (step) => {
    setEditingStepKey(step._key);
    setStepForm({
      StepOrder: step.StepOrder ?? 1,
      OffsetDays: step.OffsetDays ?? "",
      OffsetBasis: step.OffsetBasis ?? "after_send",
      Channel: step.Channel ?? "email",
      Subject: step.Subject ?? "",
      Body: step.Body ?? "",
      FlagActive: Number(step.FlagActive) ? 1 : 0,
      Id: step.Id,
    });
    setStepModal(true);
  };

  const handleSaveStep = () => {
    if (stepForm.OffsetDays === "" || Number.isNaN(Number(stepForm.OffsetDays))) {
      toast.error("Offset days is required");
      return;
    }
    const row = {
      Id: stepForm.Id, // undefined for a new step
      StepOrder: Number(stepForm.StepOrder) || 1,
      OffsetDays: Number(stepForm.OffsetDays) || 0,
      OffsetBasis: stepForm.OffsetBasis,
      Channel: stepForm.Channel,
      Subject: stepForm.Subject,
      Body: stepForm.Body,
      FlagActive: stepForm.FlagActive,
    };
    if (editingStepKey != null) {
      setSteps((prev) =>
        prev.map((s) => (s._key === editingStepKey ? { _key: s._key, ...row } : s))
      );
    } else {
      setSteps((prev) => [...prev, { _key: nextStepKey.current++, ...row }]);
    }
    setStepModal(false);
  };

  const handleRemoveStep = (step) =>
    setSteps((prev) => prev.filter((s) => s._key !== step._key));

  const handleSave = async () => {
    if (!form.Name?.trim()) {
      toast.error("Schedule name is required");
      return;
    }
    if (steps.length === 0) {
      toast.error("Add at least one reminder step");
      return;
    }
    const payload = {
      Name: form.Name,
      Description: form.Description,
      FlagDefault: form.FlagDefault,
      FlagActive: form.FlagActive,
      MaxReminders:
        form.MaxReminders === "" ? null : Number(form.MaxReminders) || null,
      esignreminderschedulestep_list: displayedSteps.map((s, index) => {
        const { _key, Id, ...rest } = s;
        const clean = { ...rest, StepOrder: index + 1 };
        if (Id) clean.Id = Id; // keep Id so existing steps update in place
        return clean;
      }),
    };
    setSaving(true);
    try {
      const res = isNew
        ? await createReminderSchedule([payload])
        : await updateReminderSchedule([{ Id: selectedId, ...payload }]);
      if (res?.status === 200) {
        toast.success(res.message || "Saved");
        await fetchList();
        const savedId = res.ESignReminderSchedule_Id ?? selectedId;
        if (savedId) {
          handleSelect(savedId);
        } else {
          resetEditor();
        }
      } else {
        toast.error(res?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteReminderSchedules([deleteTarget]);
      if (res?.status === 200) {
        toast.success(res.message || "Deleted");
        setDeleteTarget(null);
        resetEditor();
        await fetchList();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } finally {
      setDeleting(false);
    }
  };

  const showEditor = isNew || selectedId;

  return (
    <div className="mt-2 pt-2">
      {deleteTarget != null && (
        <DeleteModal
          show={deleteTarget != null}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteTarget(null)}
          loading={deleting}
          customText="Delete this reminder schedule? Envelopes using it will stop sending reminders."
        />
      )}

      <Row>
        {/* Schedule list */}
        <Col md={4} lg={3}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0">Reminder Schedules</h6>
            <Button color="success" size="sm" onClick={handleNew}>
              <i className="ri-add-line align-bottom" /> New
            </Button>
          </div>
          <p className="text-muted small">
            Reusable chase sequences (e.g. 3 / 7 / 14 days). Pick one when sending
            an e-sign envelope; the default is applied automatically.
          </p>
          <div className="list-group">
            {loadingList && (
              <div className="text-muted p-2">Loading…</div>
            )}
            {!loadingList && scheduleList.length === 0 && !isNew && (
              <div className="text-muted p-2">
                No reminder schedules yet. Create one to get started.
              </div>
            )}
            {isNew && (
              <div className="list-group-item active">
                <span className="fst-italic">New schedule…</span>
              </div>
            )}
            {scheduleList.map((schedule) => (
              <button
                key={schedule.Id}
                type="button"
                className={`list-group-item list-group-item-action ${
                  Number(schedule.Id) === Number(selectedId) ? "active" : ""
                }`}
                onClick={() => handleSelect(schedule.Id)}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span className="text-truncate">{schedule.Name}</span>
                  <span className="d-flex gap-1 ms-1">
                    {Number(schedule.FlagDefault) === 1 && (
                      <Badge color="info">Default</Badge>
                    )}
                    {Number(schedule.StepCount) > 0 && (
                      <Badge color="light" className="text-dark">
                        {schedule.StepCount} step
                        {Number(schedule.StepCount) === 1 ? "" : "s"}
                      </Badge>
                    )}
                    {Number(schedule.FlagActive) !== 1 && (
                      <Badge color="secondary">Inactive</Badge>
                    )}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Col>

        {/* Editor */}
        <Col md={8} lg={9}>
          {!showEditor ? (
            <div className="text-muted p-3">
              Select a schedule on the left, or create a new one, to manage its
              details and steps.
            </div>
          ) : (
            <>
              <div className="border rounded p-3 mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0">
                    {isNew ? "New Schedule" : "Schedule Details"}
                  </h6>
                  {!isNew && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => setDeleteTarget(selectedId)}
                    >
                      <i className="ri-delete-bin-line align-bottom" /> Delete
                    </Button>
                  )}
                </div>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Name</Label>
                      <Input
                        type="text"
                        value={form.Name}
                        onChange={(e) => setField("Name", e.target.value)}
                        placeholder="e.g. Standard chase"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        type="text"
                        value={form.Description}
                        onChange={(e) => setField("Description", e.target.value)}
                        placeholder="e.g. 3 / 7 / 14 day reminders"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <Col md="auto">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="schFlagActive"
                        checked={form.FlagActive === 1}
                        onChange={(e) =>
                          setField("FlagActive", e.target.checked ? 1 : 0)
                        }
                      />
                      <Label className="form-check-label" htmlFor="schFlagActive">
                        Active
                      </Label>
                    </div>
                  </Col>
                  <Col md="auto">
                    <div className="form-check form-switch">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="schFlagDefault"
                        checked={form.FlagDefault === 1}
                        onChange={(e) =>
                          setField("FlagDefault", e.target.checked ? 1 : 0)
                        }
                      />
                      <Label className="form-check-label" htmlFor="schFlagDefault">
                        Default schedule
                      </Label>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="d-flex align-items-center gap-2">
                      <Label className="mb-0 text-nowrap">Max reminders</Label>
                      <Input
                        type="number"
                        min={1}
                        value={form.MaxReminders}
                        onChange={(e) => setField("MaxReminders", e.target.value)}
                        placeholder="all steps"
                      />
                    </div>
                  </Col>
                </Row>
                <div className="mt-2">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : isNew ? "Create Schedule" : "Save Changes"}
                  </Button>
                  {isNew && (
                    <Button
                      color="light"
                      size="sm"
                      className="ms-2"
                      onClick={resetEditor}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Steps */}
              <div className="border rounded p-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="mb-0">Steps</h6>
                  <Button color="success" size="sm" onClick={handleNewStep}>
                    <i className="ri-add-line align-bottom" /> Add Step
                  </Button>
                </div>
                {displayedSteps.length === 0 ? (
                  <div className="text-muted p-2">
                    No steps yet. Add the first reminder of this chase sequence.
                  </div>
                ) : (
                  <Table responsive size="sm" className="align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>When</th>
                        <th>Channel</th>
                        <th>Subject</th>
                        <th>Active</th>
                        <th style={{ width: "90px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedSteps.map((step, index) => (
                        <tr key={step._key}>
                          <td>{index + 1}</td>
                          <td>{stepSummary(step)}</td>
                          <td>
                            <Badge color="primary" pill>
                              {CHANNEL_OPTIONS.find(
                                (c) => c.value === step.Channel
                              )?.label ?? step.Channel}
                            </Badge>
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 220 }}>
                            {step.Subject || (
                              <span className="text-muted">(default)</span>
                            )}
                          </td>
                          <td>
                            {Number(step.FlagActive) === 1 ? (
                              <Badge color="success">Yes</Badge>
                            ) : (
                              <Badge color="secondary">No</Badge>
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              color="light"
                              size="sm"
                              className="me-1"
                              title="Edit step"
                              onClick={() => handleEditStep(step)}
                            >
                              <i className="ri-pencil-line" />
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              title="Remove step"
                              onClick={() => handleRemoveStep(step)}
                            >
                              <i className="ri-delete-bin-line text-danger" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                <small className="text-muted">
                  Message tokens: {"{{SignerName}}"}, {"{{EnvelopeName}}"},{" "}
                  {"{{SignUrl}}"}, {"{{ExpiryDate}}"}, {"{{SenderName}}"}. Leave
                  subject/body blank to use a default reminder message.
                </small>
              </div>
            </>
          )}
        </Col>
      </Row>

      {/* Step create/edit modal */}
      <Modal isOpen={stepModal} toggle={() => setStepModal(!stepModal)} centered>
        <ModalHeader toggle={() => setStepModal(false)}>
          {editingStepKey != null ? "Edit Step" : "Add Step"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col xs={4}>
              <FormGroup>
                <Label>Order</Label>
                <Input
                  type="number"
                  min={1}
                  value={stepForm.StepOrder}
                  onChange={(e) => setStepField("StepOrder", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label>Offset (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={stepForm.OffsetDays}
                  onChange={(e) => setStepField("OffsetDays", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label>Relative to</Label>
                <Select
                  options={BASIS_OPTIONS}
                  value={BASIS_OPTIONS.find(
                    (o) => o.value === stepForm.OffsetBasis
                  )}
                  onChange={(option) => setStepField("OffsetBasis", option.value)}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Channel</Label>
            <Select
              options={CHANNEL_OPTIONS}
              value={CHANNEL_OPTIONS.find((o) => o.value === stepForm.Channel)}
              onChange={(option) => setStepField("Channel", option.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Subject (email)</Label>
            <Input
              type="text"
              value={stepForm.Subject}
              onChange={(e) => setStepField("Subject", e.target.value)}
              placeholder="Reminder: please sign {{EnvelopeName}}"
            />
          </FormGroup>
          <FormGroup>
            <Label>Message</Label>
            <Input
              type="textarea"
              rows={4}
              value={stepForm.Body}
              onChange={(e) => setStepField("Body", e.target.value)}
              placeholder="A quick reminder that {{EnvelopeName}} is awaiting your signature."
            />
            <small className="text-muted">
              Tokens: {"{{SignerName}}"}, {"{{EnvelopeName}}"}, {"{{SignUrl}}"},{" "}
              {"{{ExpiryDate}}"}, {"{{SenderName}}"}
            </small>
          </FormGroup>
          <div className="form-check form-switch">
            <Input
              type="checkbox"
              className="form-check-input"
              id="stepFlagActive"
              checked={stepForm.FlagActive === 1}
              onChange={(e) =>
                setStepField("FlagActive", e.target.checked ? 1 : 0)
              }
            />
            <Label className="form-check-label" htmlFor="stepFlagActive">
              Active
            </Label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setStepModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveStep}>
            {editingStepKey != null ? "Save Step" : "Add Step"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
